import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { Gastos, GastosPagingRes } from '@/shared/interfaces/erp/inventario/gastos.interface';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchGastos = (params?: getGastosParams, enabled = true) => {
  return useQuery({
    queryKey: ['Gastos', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getGastos(params);
    },
    enabled,
  });
};

export const useGetGasto = (id: string) => {
  return useQuery({
    queryKey: ['Gastos', id],
    queryFn: async () => {
      return getGasto(id);
    },
  });
};

export const useCreateGasto = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createGasto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Gastos'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'gasto creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateGasto = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateGasto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Gastos'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'gasto actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemoveGasto = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeGasto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Gastos'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type getGastosParams = Partial<Gastos> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateGastoParams = Omit<Gastos, 'id_gasto'>;
export type UpdateGastoParams = {
  id: string;
  data: CreateGastoParams;
};

export const getGastos = async (params?: getGastosParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<GastosPagingRes>(`${urlBackend}api/v1/gasto/?${queryParams}`, true);
};

export const getGasto = async (id: string) => {
  try {
    return await get<Gastos>(`${urlBackend}api/v1/gasto/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createGasto = async (data: CreateGastoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Gastos>(`${urlBackend}api/v1/gasto/`, data, true);
};

export const updateGasto = async ({ id, data }: UpdateGastoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Gastos>(`${urlBackend}api/v1/gasto/${id}/`, data, true);
};

export const removeGasto = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<Gastos>(`${urlBackend}api/v1/gasto/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};